import type {
  AccessLevelProp,
  BadgeGroupItemProps,
  CalloutProps,
  IconName,
  IconProps,
  ShortcutProps,
  ToggleInputProps,
} from '@meterup/atto';
import type { To } from 'react-router';
import {
  AccessLevel,
  AlignStack,
  Badge,
  BadgeGroup,
  BodySansSizes,
  Callout,
  colors,
  containers,
  darkThemeSelector,
  devices,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuToggleItem,
  DropdownMenuTrigger,
  fade,
  FeatureBadge,
  Icon,
  palette,
  selectors,
  shadows,
  Shortcut,
  stateZIndexes,
  styled,
  Text,
  Tooltip,
  transitions,
  useAccessLevelContext,
  useCanHover,
  UtilityControl,
} from '@meterup/atto';
import {
  type ElementType,
  type EventHandler,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
  useMemo,
  useState,
} from 'react';
import { mergeRefs } from 'react-merge-refs';

import { type SigilProps, Sigil } from '../../Sigil';
import { useSidebarContext } from './utils';
import { sidebarVars } from './vars';

const sidebarTargetMediaVars = {
  size: {
    minimized: {
      mouse: 16,
      touch: 18,
    },
    maximized: {
      mouse: 14,
      touch: 16,
    },
    narrow: 18,
  },
};

type SidebarTargetMediaProps = {
  icon?: IconProps;
  sigil?: Omit<SigilProps, 'size'>;
  minimized?: boolean;
};

const SidebarTargetMediaSigil = styled(Sigil, {
  variants: {
    minimized: {
      true: {
        [devices.mouse]: {
          size: `$${sidebarTargetMediaVars.size.minimized.mouse + 2}`,
        },

        [devices.touch]: {
          size: `$${sidebarTargetMediaVars.size.minimized.touch + 2}`,
        },
      },
      false: {
        [devices.mouse]: {
          size: `$${sidebarTargetMediaVars.size.maximized.mouse + 2}`,
        },

        [devices.touch]: {
          size: `$${sidebarTargetMediaVars.size.maximized.touch + 2}`,
        },

        [containers.application.narrow]: {
          size: `$${sidebarTargetMediaVars.size.narrow + 2}`,
        },
      },
    },
  },
});
SidebarTargetMediaSigil.displayName = 'SidebarTargetMediaSigil';

const SidebarTargetMediaIcon = styled(Icon, {
  display: 'flex',

  variants: {
    minimized: {
      true: {
        [devices.mouse]: {
          size: `$${sidebarTargetMediaVars.size.minimized.mouse}`,
        },

        [devices.touch]: {
          size: `$${sidebarTargetMediaVars.size.minimized.touch}`,
        },
      },
      false: {
        [devices.mouse]: {
          size: `$${sidebarTargetMediaVars.size.maximized.mouse}`,
        },

        [devices.touch]: {
          size: `$${sidebarTargetMediaVars.size.maximized.touch}`,
        },

        [containers.application.narrow]: {
          size: `$${sidebarTargetMediaVars.size.narrow}`,
        },
      },
    },
  },
});
SidebarTargetMediaIcon.displayName = 'SidebarTargetMediaIcon';

const SidebarTargetMediaContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '$$sidebarTargetMediaColor',

  variants: {
    minimized: {
      true: {
        [devices.mouse]: {
          widthAll: '$20',
        },

        [devices.touch]: {
          widthAll: '$24',
        },
      },
      false: {
        [devices.mouse]: {
          widthAll: '$14',
        },

        [devices.touch]: {
          widthAll: '$18',
        },
      },
    },
  },
});
SidebarTargetMediaContainer.displayName = 'SidebarTargetMediaContainer';

function SidebarTargetMedia({ icon, sigil, minimized }: SidebarTargetMediaProps) {
  let rendered = null;
  if (sigil) {
    rendered = <SidebarTargetMediaSigil minimized={minimized} {...sigil} />;
  }
  if (icon) {
    rendered = <SidebarTargetMediaIcon minimized={minimized} {...icon} />;
  }

  return (
    <SidebarTargetMediaContainer minimized={minimized}>{rendered}</SidebarTargetMediaContainer>
  );
}

type SidebarTargetAnnotationsBadgesProps = {
  accessLevel?: AccessLevelProp | null;
  badges?: BadgeGroupItemProps[];
  bubbled?: boolean;
};

const SidebarTargetAnnotationsBadgesContainer = styled(BadgeGroup, {
  variants: {
    bubbled: {
      true: {
        position: 'absolute',
        zIndex: stateZIndexes.fixture,
        top: '$6',
        right: '$6',
        transform: 'translate(50%, -50%)',
      },
      false: {},
    },
  },
});
SidebarTargetAnnotationsBadgesContainer.displayName = 'SidebarTargetAnnotationsBadgesContainer';

function SidebarTargetAnnotationsBadges({
  accessLevel: accessLevelProp,
  badges,
  bubbled = false,
}: SidebarTargetAnnotationsBadgesProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);

  const hasBadges = Boolean(badges?.length || accessLevel);

  if (!hasBadges) return null;

  return (
    <SidebarTargetAnnotationsBadgesContainer size="x-small" bubbled={bubbled}>
      {badges?.map((badge, index) => (
        <Badge key={`${String(badge.icon ?? 'badge')}-${String(index)}`} {...badge} />
      ))}
      {accessLevel && <FeatureBadge type={accessLevel} size="x-small" />}
    </SidebarTargetAnnotationsBadgesContainer>
  );
}

type SidebarTargetAnnotationsProps = {
  accessLevel?: AccessLevelProp | null;
  badges?: BadgeGroupItemProps[];
  shortcut?: ShortcutProps;
};

const SidebarTargetAnnotationsContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: `$${sidebarVars.gap}`,
});
SidebarTargetAnnotationsContainer.displayName = 'SidebarTargetAnnotationsContainer';

function SidebarTargetAnnotations({
  accessLevel: accessLevelProp,
  badges,
  shortcut,
}: SidebarTargetAnnotationsProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);

  return (
    <SidebarTargetAnnotationsContainer>
      {shortcut && <Shortcut {...shortcut} />}
      <SidebarTargetAnnotationsBadges accessLevel={accessLevel} badges={badges} />
    </SidebarTargetAnnotationsContainer>
  );
}

type SidebarTargetArrowsTypeProp = 'dropdown' | 'toggle';

type SidebarTargetArrowsProps = {
  isOpen?: boolean;
  type?: SidebarTargetArrowsTypeProp;
};

const SidebarTargetArrowsContainer = styled(Icon, {
  size: '$10',
  color: '$$sidebarTargetArrowsColor',

  [devices.mouse]: {
    size: '$10',
  },

  [devices.touch]: {
    size: '$12',
  },
});
SidebarTargetArrowsContainer.displayName = 'SidebarTargetArrowsContainer';

function SidebarTargetArrows({ type = 'dropdown', isOpen }: SidebarTargetArrowsProps) {
  let icon: IconName = 'chevron-down';
  if (type === 'toggle') {
    icon = 'chevron-right';
  }
  if (type === 'toggle' && isOpen) {
    icon = 'chevron-down';
  }
  return <SidebarTargetArrowsContainer icon={icon} />;
}

type SidebarTargetActionsBase = {
  key: string;
  label: ReactNode;
  group?: string;
  accessLevel?: AccessLevel | null;
  icon?: IconName;
  disabled?: boolean;
  variant?: 'neutral' | 'negative';
};

type SidebarTargetActionsItem = SidebarTargetActionsBase & {
  type?: 'item';
  onClick?: () => void;
  to?: string;
  as?: React.ElementType;
};

type SidebarTargetActionsCheckbox = SidebarTargetActionsBase & {
  type: 'checkbox';
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  hideCheckmark?: boolean;
};

type SidebarTargetActionsToggle = SidebarTargetActionsBase & {
  type: 'toggle';
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  description?: ReactNode;
  keepOpenOnToggle?: boolean;
  toggleProps?: Pick<ToggleInputProps, 'variant' | 'size'>;
};

type SidebarTargetActionsRadio = SidebarTargetActionsBase & {
  type: 'radio';
  value: string;
  // Optional props for radio group state management (if all items in group are radio)
  radioGroupValue?: string;
  radioGroupOnValueChange?: (value: string) => void;
};

export type SidebarTargetActions =
  | SidebarTargetActionsItem
  | SidebarTargetActionsCheckbox
  | SidebarTargetActionsToggle
  | SidebarTargetActionsRadio;

interface SidebarTargetActionsMenuProps {
  actions?: SidebarTargetActions[];
}

// Helper function to render a regular item
function ActionItem({ action }: { action: SidebarTargetActionsItem }) {
  const commonProps = {
    key: action.key,
    accessLevel: action.accessLevel,
    onClick: action.onClick,
    // Base UI's keyboard handler (useButton.onKeyDown) calls externalOnKeyDown first, before
    // its own Enter logic. Our onClick isn't passed to Menu.Item so it never reaches
    // externalOnClick — this onKeyDown is the only way to fire the action via keyboard.
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        action.onClick?.();
      }
    },
    icon: action.icon as any,
    disabled: action.disabled,
    variant: action.variant,
  };

  if (action.to || action.as) {
    return (
      <DropdownMenuItem {...commonProps} to={action.to} as={action.as || 'div'}>
        {action.label}
      </DropdownMenuItem>
    );
  }

  return <DropdownMenuItem {...commonProps}>{action.label}</DropdownMenuItem>;
}

// Helper function to render an action based on its type
function renderAction(action: SidebarTargetActions): ReactNode {
  switch (action.type) {
    case 'checkbox':
      return <DropdownMenuCheckboxItem {...action} />;
    case 'toggle':
      return <DropdownMenuToggleItem {...action} />;
    case 'radio':
      return <DropdownMenuRadioItem {...action} />;
    default:
      return <ActionItem action={action} />;
  }
}

export function SidebarTargetActionsMenu({ actions: actionsProp }: SidebarTargetActionsMenuProps) {
  const grouped = useMemo(() => {
    if (!actionsProp) return [];
    const map = new Map<string | undefined, SidebarTargetActions[]>();
    for (const action of actionsProp) {
      if (action.accessLevel === null) continue;
      const key = action.group ?? undefined;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(action);
    }
    return Array.from(map.entries());
  }, [actionsProp]);

  const groups: ReactNode[] = [];
  for (const [groupKey, groupActions] of grouped) {
    if (groupActions.length === 0) continue;
    const groupKeyOrDefault = groupKey ?? 'default';
    const isRadioGroup = groupActions.every((a) => a.type === 'radio');
    const radioAction = isRadioGroup ? (groupActions[0] as SidebarTargetActionsRadio) : undefined;
    const hasRadioGroupState =
      radioAction?.radioGroupValue !== undefined && radioAction?.radioGroupOnValueChange;

    if (isRadioGroup && hasRadioGroupState) {
      groups.push(
        <DropdownMenuRadioGroup
          key={groupKeyOrDefault}
          label={groupKey}
          value={radioAction.radioGroupValue}
          onValueChange={radioAction.radioGroupOnValueChange!}
        >
          {groupActions.map((action) =>
            action.type === 'radio' ? <DropdownMenuRadioItem {...action} /> : null,
          )}
        </DropdownMenuRadioGroup>,
      );
    } else {
      groups.push(
        <DropdownMenuGroup key={groupKeyOrDefault} label={groupKey}>
          {groupActions.map(renderAction)}
        </DropdownMenuGroup>,
      );
    }
  }

  return groups;
}
export type SidebarTargetProps = {
  accessLevel?: AccessLevelProp | null;
  actions?: SidebarTargetActions[];
  annotations?: SidebarTargetAnnotationsProps;
  arrangement?: 'hidden-label' | 'leading-icon';
  arrows?: SidebarTargetArrowsTypeProp;
  callout?: Omit<CalloutProps, 'position'>;
  id?: string;
  internal?: boolean;
  isOpen?: boolean;
  label: ReactNode;
  media?: SidebarTargetMediaProps;
  minimized?: boolean;
  /** Merged onto the root container (e.g. Base UI `NavigationMenu.Trigger` render props). */
  composeProps?: HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLElement | null> };
  /** When minimized, skip the label tooltip (e.g. when a flyout provides branch links). */
  suppressMinimizedTooltip?: boolean;
  /**
   * On narrow layouts, the slide-out menu stays open after activating this link so the user can
   * pick a destination in a nested sidebar (e.g. Settings / Toolshed roots).
   */
  keepMenuOpenOnNavigate?: boolean;
  selected?: boolean;
  /** Node rendered inside SidebarTargetOverflow (e.g. a drag handle for touch devices). */
  dragHandle?: ReactNode;
  // Routing & interaction
  as?: ElementType;
  onClick?: MouseEventHandler<HTMLElement>;
  ref?: RefObject<HTMLDivElement | null>;
  to?: To;
};

function mergeHandler<E extends SyntheticEvent>(
  a: EventHandler<E> | undefined,
  b: EventHandler<E> | undefined,
): EventHandler<E> | undefined {
  if (!a) return b;
  if (!b) return a;
  return (event: E) => {
    a(event);
    if (!event.isPropagationStopped()) {
      b(event);
    }
  };
}

const SidebarTargetLabel = styled(Text, {
  display: 'block',
  minWidth: 0,
  fontWeight: '$bold',
  color: '$$sidebarTargetLabelColor',
  ...BodySansSizes,
});
SidebarTargetLabel.displayName = 'SidebarTargetLabel';

const SidebarTargetEnd = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: `$${sidebarVars.gap}`,
});
SidebarTargetEnd.displayName = 'SidebarTargetEnd';

const SidebarTargetStack = styled(AlignStack, {
  position: 'relative',

  variants: {
    minimized: {
      true: {
        [devices.mouse]: {
          padding: '$4 0',
        },

        [devices.touch]: {
          padding: '$6 0',
        },
      },
      false: {
        [devices.mouse]: {
          padding: '$4 $8',
        },

        [devices.touch]: {
          padding: '$6 $8',
        },
      },
    },
  },
});
SidebarTargetStack.displayName = 'SidebarTargetStack';

const SidebarTargetTrigger = styled('div', {
  position: 'relative',
  zIndex: stateZIndexes.initial,
  display: 'flex',
  background: '$$sidebarTargetBackground',
  borderRadius: '$8',
  strokeAll: '$$sidebarTargetStroke',
  transition: transitions.control,

  variants: {
    arrangement: {
      'hidden-label': {},
      'leading-icon': {
        width: '100%',
        minWidth: 0,
      },
    },
    internal: {
      true: {},
      false: {},
    },
    isPressable: {
      true: {
        cursor: 'pointer',
        userSelect: 'none',
      },
      false: {
        cursor: 'default',
      },
    },
    minimized: {
      true: {
        width: '100%',
      },
      false: {},
    },
    selected: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      arrangement: 'hidden-label',
      minimized: false,
      css: {
        width: 'min-content',
      },
    },
    {
      isPressable: true,
      css: {
        [selectors.interactive]: {
          zIndex: stateZIndexes.hover,
        },

        [selectors.focus]: {
          zIndex: stateZIndexes.focus,
          outline: 'none',
          boxShadow: shadows.focusRingLight,

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,
          },
        },
      },
    },
    {
      isPressable: true,
      selected: true,
      css: {
        zIndex: stateZIndexes.selected,
      },
    },
  ],
});
SidebarTargetTrigger.displayName = 'SidebarTargetTrigger';

const SidebarTargetOverflow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$2',
  padding: '$2 $8',
  borderRadius: '0 $8 $8 0',
  pointerEvents: 'none',
  transition: transitions.control,

  '& > *': {
    pointerEvents: 'auto',
  },

  [devices.mouse]: {
    position: 'absolute',
    zIndex: stateZIndexes.fixture,
    top: '50%',
    right: 0,
    background: '$$sidebarTargetOverflowBackground',
    transform: 'translateY(-50%)',
    opacity: '$$sidebarTargetOverflowOpacity',
  },
});
SidebarTargetOverflow.displayName = 'SidebarTargetOverflow';

const SidebarTargetContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  $$sidebarTargetOverflowOpacity: 0,

  [selectors.interactive]: {
    $$sidebarTargetOverflowOpacity: 1,
  },

  $$sidebarTargetMediaColor: colors.iconNeutralLight,
  $$sidebarTargetLabelColor: colors.bodyNeutralLight,
  $$sidebarTargetArrowsColor: colors.iconNeutralLight,
  $$sidebarTargetOverflowBackground: `linear-gradient(to right, ${fade(palette.bgNeutralLight, 0)}, ${colors.bgNeutralLight} 20%)`,

  [darkThemeSelector]: {
    $$sidebarTargetMediaColor: colors.iconNeutralDark,
    $$sidebarTargetLabelColor: colors.bodyNeutralDark,
    $$sidebarTargetArrowsColor: colors.iconNeutralDark,
    $$sidebarTargetOverflowBackground: `linear-gradient(to right, ${fade(palette.bgNeutralDark, 0)}, ${colors.bgNeutralDark} 20%)`,
  },

  // Sortable wrappers flag an active drag with this attribute. Brand the icon/label/arrows to
  // match the selected state, since the wrapper can only supply the background/stroke vars —
  // the neutral values above would otherwise shadow anything it sets for these.
  '[data-sidebar-target-dragging] &': {
    $$sidebarTargetMediaColor: colors.bodyBrandLight,
    $$sidebarTargetLabelColor: colors.headingBrandLight,
    $$sidebarTargetArrowsColor: colors.iconBrandLight,

    [darkThemeSelector]: {
      $$sidebarTargetMediaColor: colors.bodyBrandDark,
      $$sidebarTargetLabelColor: colors.headingBrandDark,
      $$sidebarTargetArrowsColor: colors.iconBrandDark,
    },
  },

  variants: {
    actionsOpen: {
      true: {},
      false: {},
    },
    arrangement: {
      'hidden-label': {},
      'leading-icon': {
        width: '100%',
        minWidth: 0,
      },
    },
    internal: {
      true: {},
      false: {},
    },
    isPressable: {
      true: {},
      false: {},
    },
    minimized: {
      true: {},
      false: {},
    },
    selected: {
      true: {
        $$sidebarTargetOverflowBackground: `linear-gradient(to right, ${fade(palette.bgBrandLight, 0)}, ${colors.bgBrandLight} 20%)`,

        [darkThemeSelector]: {
          $$sidebarTargetOverflowBackground: `linear-gradient(to right, ${fade(palette.bgBrandDark, 0)}, ${colors.bgBrandDark} 20%)`,
        },
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      arrangement: 'hidden-label',
      minimized: false,
      css: {
        width: 'min-content',
      },
    },
    {
      isPressable: true,
      css: {
        [selectors.interactive]: {
          zIndex: stateZIndexes.hover,

          $$sidebarTargetBackground: colors.bgNeutralLight,
          $$sidebarTargetMediaColor: colors.bodyNeutralLight,
          $$sidebarTargetLabelColor: colors.headingNeutralLight,
          $$sidebarTargetArrowsColor: colors.iconNeutralLight,

          [darkThemeSelector]: {
            $$sidebarTargetBackground: colors.bgNeutralDark,
            $$sidebarTargetMediaColor: colors.bodyNeutralDark,
            $$sidebarTargetLabelColor: colors.headingNeutralDark,
            $$sidebarTargetArrowsColor: colors.iconNeutralDark,
          },
        },

        [selectors.focus]: {
          zIndex: stateZIndexes.focus,
          outline: 'none',

          $$sidebarTargetShadow: shadows.focusRingLight,
          $$sidebarTargetMediaColor: colors.bodyBrandLight,
          $$sidebarTargetLabelColor: colors.headingBrandLight,
          $$sidebarTargetArrowsColor: colors.iconBrandLight,

          [darkThemeSelector]: {
            $$sidebarTargetShadow: shadows.focusRingDark,
            $$sidebarTargetMediaColor: colors.bodyBrandDark,
            $$sidebarTargetLabelColor: colors.headingBrandDark,
            $$sidebarTargetArrowsColor: colors.iconBrandDark,
          },
        },
      },
    },
    {
      isPressable: true,
      selected: true,
      css: {
        zIndex: stateZIndexes.selected,

        [selectors.all]: {
          $$sidebarTargetBackground: colors.bgBrandLight,
          $$sidebarTargetStroke: colors.strokeBrandLight,
          $$sidebarTargetMediaColor: colors.bodyBrandLight,
          $$sidebarTargetLabelColor: colors.headingBrandLight,
          $$sidebarTargetArrowsColor: colors.iconBrandLight,

          [darkThemeSelector]: {
            $$sidebarTargetBackground: colors.bgBrandDark,
            $$sidebarTargetStroke: colors.strokeBrandDark,
            $$sidebarTargetMediaColor: colors.bodyBrandDark,
            $$sidebarTargetLabelColor: colors.headingBrandDark,
            $$sidebarTargetArrowsColor: colors.iconBrandDark,
          },
        },
      },
    },
    {
      internal: true,
      css: {
        $$sidebarTargetMediaColor: colors.internalIconLight,
        $$sidebarTargetLabelColor: colors.internalBodyLight,
        $$sidebarTargetArrowsColor: colors.internalIconLight,
        $$sidebarTargetOverflowBackground: `linear-gradient(to right, ${fade(palette.internalBgLight, 0)}, ${colors.internalBgLight} 20%)`,

        [darkThemeSelector]: {
          $$sidebarTargetMediaColor: colors.internalIconDark,
          $$sidebarTargetLabelColor: colors.internalBodyDark,
          $$sidebarTargetArrowsColor: colors.internalIconDark,
          $$sidebarTargetOverflowBackground: `linear-gradient(to right, ${fade(palette.internalBgDark, 0)}, ${colors.internalBgDark} 20%)`,
        },

        [selectors.interactive]: {
          $$sidebarTargetBackground: colors.internalBgLight,
          $$sidebarTargetMediaColor: colors.internalBodyLight,
          $$sidebarTargetLabelColor: colors.internalHeadingLight,
          $$sidebarTargetArrowsColor: colors.internalIconLight,

          [darkThemeSelector]: {
            $$sidebarTargetBackground: colors.internalBgDark,
            $$sidebarTargetMediaColor: colors.internalBodyDark,
            $$sidebarTargetLabelColor: colors.internalHeadingDark,
            $$sidebarTargetArrowsColor: colors.internalIconDark,
          },
        },
      },
    },
    {
      internal: true,
      isPressable: true,
      selected: true,
      css: {
        [selectors.all]: {
          $$sidebarTargetBackground: colors.internalBgLight,
          $$sidebarTargetStroke: colors.internalStrokeLight,
          $$sidebarTargetMediaColor: colors.internalBodyLight,
          $$sidebarTargetLabelColor: colors.internalHeadingLight,
          $$sidebarTargetArrowsColor: colors.internalIconLight,

          [darkThemeSelector]: {
            $$sidebarTargetBackground: colors.internalBgDark,
            $$sidebarTargetStroke: colors.internalStrokeDark,
            $$sidebarTargetMediaColor: colors.internalBodyDark,
            $$sidebarTargetLabelColor: colors.internalHeadingDark,
            $$sidebarTargetArrowsColor: colors.internalIconDark,
          },
        },
      },
    },
    {
      isPressable: true,
      selected: false,
      actionsOpen: true,
      css: {
        $$sidebarTargetOverflowOpacity: 1,
        zIndex: stateZIndexes.hover,
        $$sidebarTargetBackground: colors.bgNeutralLight,
        $$sidebarTargetMediaColor: colors.bodyNeutralLight,
        $$sidebarTargetLabelColor: colors.headingNeutralLight,
        $$sidebarTargetArrowsColor: colors.iconNeutralLight,

        [darkThemeSelector]: {
          $$sidebarTargetBackground: colors.bgNeutralDark,
          $$sidebarTargetMediaColor: colors.bodyNeutralDark,
          $$sidebarTargetLabelColor: colors.headingNeutralDark,
          $$sidebarTargetArrowsColor: colors.iconNeutralDark,
        },
      },
    },
    {
      isPressable: true,
      internal: true,
      selected: false,
      actionsOpen: true,
      css: {
        $$sidebarTargetOverflowOpacity: 1,
        $$sidebarTargetBackground: colors.internalBgLight,
        $$sidebarTargetMediaColor: colors.internalBodyLight,
        $$sidebarTargetLabelColor: colors.internalHeadingLight,
        $$sidebarTargetArrowsColor: colors.internalIconLight,

        [darkThemeSelector]: {
          $$sidebarTargetBackground: colors.internalBgDark,
          $$sidebarTargetMediaColor: colors.internalBodyDark,
          $$sidebarTargetLabelColor: colors.internalHeadingDark,
          $$sidebarTargetArrowsColor: colors.internalIconDark,
        },
      },
    },
  ],
});
SidebarTargetContainer.displayName = 'SidebarTargetContainer';

export function SidebarTarget({
  accessLevel: accessLevelProp,
  actions,
  annotations,
  arrangement = 'leading-icon',
  arrows,
  callout,
  composeProps,
  dragHandle,
  id,
  internal = false,
  isOpen,
  label,
  media,
  minimized,
  selected,
  suppressMinimizedTooltip,
  keepMenuOpenOnNavigate = false,
  // Routing
  as,
  onClick,
  to,
  ref,
  ...remaining
}: SidebarTargetProps) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const canHover = useCanHover();
  const { minimized: sidebarMinimized, menuOpen, setMenuOpen, isNarrow } = useSidebarContext();
  const effectiveMinimized = minimized ?? sidebarMinimized;
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isLink = Boolean(as && to);
  const isPressable = Boolean(onClick || as);
  const isInternal = internal || accessLevel === AccessLevel.Internal;
  const closeMenuAfterNav =
    isLink && isNarrow && menuOpen && !keepMenuOpenOnNavigate
      ? (event: MouseEvent<HTMLElement>) => {
          // Tree roots call preventDefault when toggling branches instead of navigating.
          if (!event.defaultPrevented) setMenuOpen(false);
        }
      : undefined;
  const rootRole = isPressable && !isLink ? 'button' : undefined;
  const showAccessBadge = Boolean(accessLevel && accessLevel !== AccessLevel.Public);
  const hasEnd =
    arrangement !== 'hidden-label' &&
    !effectiveMinimized &&
    (Boolean(annotations) || Boolean(arrows) || showAccessBadge);

  const {
    ref: composeRef,
    onClick: composeOnClick,
    onMouseEnter: composeOnMouseEnter,
    onFocus: composeOnFocus,
    onBlur: composeOnBlur,
    onKeyDown: composeOnKeyDown,
    onPointerEnter: composeOnPointerEnter,
    onPointerDown: composeOnPointerDown,
    tabIndex: composeTabIndex,
    ...composeRest
  } = composeProps ?? {};

  const render = (
    <SidebarTargetContainer
      id={id}
      role={rootRole}
      actionsOpen={actionsOpen}
      arrangement={arrangement}
      internal={isInternal}
      isPressable={isPressable}
      minimized={minimized}
      selected={selected}
    >
      <SidebarTargetTrigger
        {...composeRest}
        {...remaining}
        id={id}
        aria-label={
          arrangement === 'hidden-label' ||
          (effectiveMinimized && !canHover && !suppressMinimizedTooltip)
            ? label
            : undefined
        }
        role={rootRole}
        tabIndex={composeTabIndex ?? (isPressable && !selected ? 0 : -1)}
        aria-current={isLink && selected ? 'page' : undefined}
        as={as}
        to={to}
        ref={mergeRefs(
          [ref, composeRef].filter(Boolean) as Parameters<typeof mergeRefs<HTMLElement>>[0],
        )}
        onClick={mergeHandler(composeOnClick, mergeHandler(onClick, closeMenuAfterNav))}
        onMouseEnter={mergeHandler(composeOnMouseEnter, undefined)}
        onFocus={mergeHandler(composeOnFocus, undefined)}
        onBlur={mergeHandler(composeOnBlur, undefined)}
        onKeyDown={mergeHandler(composeOnKeyDown, undefined)}
        onPointerEnter={mergeHandler(composeOnPointerEnter, undefined)}
        onPointerDown={mergeHandler(composeOnPointerDown, undefined)}
        arrangement={arrangement}
        internal={isInternal}
        isPressable={isPressable}
        minimized={effectiveMinimized}
        selected={selected}
      >
        {(arrangement === 'hidden-label' || effectiveMinimized) && (
          <SidebarTargetAnnotationsBadges accessLevel={null} badges={annotations?.badges} bubbled />
        )}
        {callout && <Callout position={{ top: 6, right: 6 }} {...callout} />}
        <SidebarTargetStack
          minimized={effectiveMinimized}
          direction="row"
          justify={effectiveMinimized ? 'center' : 'start'}
          gap={arrangement === 'hidden-label' ? 4 : 8}
          preset="body"
          width={arrangement === 'hidden-label' && !effectiveMinimized ? 'min-content' : '100%'}
          start={media && <SidebarTargetMedia minimized={effectiveMinimized} {...media} />}
          end={
            hasEnd && (
              <SidebarTargetEnd>
                {(annotations || showAccessBadge) && (
                  <SidebarTargetAnnotations accessLevel={accessLevel} {...(annotations ?? {})} />
                )}
                {arrows && (
                  <>
                    {dragHandle}
                    <SidebarTargetArrows type={arrows} isOpen={isOpen} />
                  </>
                )}
              </SidebarTargetEnd>
            )
          }
        >
          {arrangement !== 'hidden-label' && !effectiveMinimized && (
            <SidebarTargetLabel truncate>{label}</SidebarTargetLabel>
          )}
        </SidebarTargetStack>
      </SidebarTargetTrigger>
      {(actions || (dragHandle && !arrows)) && (
        <SidebarTargetOverflow>
          {dragHandle}
          {actions && (
            <DropdownMenu open={actionsOpen} onOpenChange={(open) => setActionsOpen(open)}>
              <DropdownMenuTrigger
                render={
                  <UtilityControl
                    icon="overflow-horizontal"
                    internal={isInternal}
                    label="Quick actions"
                    variant={selected ? 'brand' : 'neutral'}
                  />
                }
              />
              <DropdownMenuPopover align="start">
                <SidebarTargetActionsMenu actions={actions} />
              </DropdownMenuPopover>
            </DropdownMenu>
          )}
        </SidebarTargetOverflow>
      )}
    </SidebarTargetContainer>
  );

  const showTooltip =
    canHover &&
    ((effectiveMinimized && !suppressMinimizedTooltip) || arrangement === 'hidden-label');

  if (showTooltip) {
    return (
      <Tooltip
        side={effectiveMinimized ? 'right' : 'bottom'}
        sideOffset={4}
        contents={
          <AlignStack
            align="center"
            gap={4}
            wrap="nowrap"
            preset="small"
            end={
              (annotations || showAccessBadge) && (
                <SidebarTargetAnnotations accessLevel={accessLevel} {...(annotations ?? {})} />
              )
            }
          >
            <Text>{label}</Text>
          </AlignStack>
        }
      >
        {render}
      </Tooltip>
    );
  }

  return render;
}
